import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const TABS = [
  { key: 'consensus', label: '共识池' },
  { key: 'fork',      label: '分叉铸币' },
  { key: 'burn',      label: '销毁合约' },
  { key: 'seed',      label: '种子合约' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const ICONS: Record<TabKey, string> = {
  consensus: '🔗',
  fork:      '🔀',
  burn:      '🔥',
  seed:      '🌱',
};

const CONTENT: Record<TabKey, { title: string; subtitle: string; paragraphs: string[] }> = {
  consensus: {
    title: 'ORB 共识池',
    subtitle: '去中心化共识驱动价值',
    paragraphs: [
      'ORB 共识池是 ORB 生态的核心价值锚定机制。通过将 ORB 代币质押进入共识池，参与者共同维护网络的价值共识。',
      '共识池采用动态权重分配算法：参与者的算力权重（net_weight）根据质押时长和数量自动调整，保证早期参与者和长期持有者获得合理回报。',
      '每日结算周期自动执行收益分配，所有分配记录可在余额日志中查询，完全透明可追溯。',
      '进入共识池的 ORB 将自动转换为 ORBC，退出时按当前汇率转回 ORB，实现灵活进出。',
    ],
  },
  fork: {
    title: '分叉铸币',
    subtitle: 'FP 积分与 FC 代币的双轨机制',
    paragraphs: [
      '分叉铸币是 ORB 生态的独创激励机制。当系统达到特定里程碑时，触发分叉事件（Fork Event），向所有符合条件的持有者分发 FP（分红积分）。',
      'FP 积分可通过参与公益捐赠、完成任务、或达到特定等级自动获得。积累到阈值后，FP 可转换为 FC（分红币），FC 是可流通的价值载体。',
      '每次分叉事件的分配比例由当时的算力权重和持仓占比共同决定，确保公平分配。',
      '历史分叉记录和您的分红明细可在「分红记录」页面查看。',
    ],
  },
  burn: {
    title: '销毁合约',
    subtitle: '通缩模型保障长期价值',
    paragraphs: [
      'ORB 采用主动销毁机制，通过智能合约定期将一定比例的流通代币永久销毁，实现通缩效果。',
      '销毁来源包括：交易手续费中的一定比例、EP 市场交易手续费、以及特定活动中的消耗。',
      '每次销毁事件的详细数据（销毁数量、触发条件、交易哈希）均会公示，任何人可验证。',
      '通缩模型结合共识池的锁仓机制，有效减少市场流通量，为 ORB 的长期价值提供支撑。',
    ],
  },
  seed: {
    title: '种子合约',
    subtitle: '早期参与者的价值承诺',
    paragraphs: [
      '种子合约面向早期参与者，提供优先参与生态建设的机会。种子轮参与者享受初始价格锁定和额外的算力加权。',
      '种子合约分为四期，每期解锁条件不同：首期立即释放 25%，剩余按季度线性释放，保证生态的健康发展。',
      '种子参与者同时获得永久的等级加速权益——升级所需的业绩门槛降低 20%，体现对早期贡献者的长期回馈。',
      '所有种子合约条款在链上公示，参与后不可篡改，确保承诺的可信性。',
    ],
  },
};

export function UoContract() {
  const { t } = useTranslation();
  const [active, setActive] = useState<TabKey>('consensus');
  const content = CONTENT[active];

  return (
    <div className="min-h-screen bg-surface-base pb-24">
      {/* Tab bar */}
      <div className="sticky top-0 z-10 bg-surface-elevated border-b border-surface-border">
        <div className="flex overflow-x-auto scrollbar-none">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActive(tab.key)}
              className={`flex-1 min-w-[80px] py-3 text-sm font-medium border-b-2 transition-colors ${
                active === tab.key
                  ? 'border-gold-500 text-gold-400'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-500/10 border border-gold-500/30 mb-4">
            <span className="text-2xl">{ICONS[active]}</span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">{content.title}</h1>
          <p className="text-sm text-text-secondary mt-1">{content.subtitle}</p>
        </div>

        {/* Paragraphs */}
        <div className="space-y-3 max-w-lg mx-auto">
          {content.paragraphs.map((p, i) => (
            <div key={i} className="bg-surface-card rounded-xl p-4 border border-surface-border">
              <div className="flex gap-3">
                <span className="shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-gold-500/20 text-[10px] font-bold text-gold-400 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-text-secondary text-sm leading-relaxed">{p}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-xs text-text-muted">
          {t('common.contentDisclaimer', '内容由平台发布 · 最终解释权归运营方所有')}
        </p>
      </div>
    </div>
  );
}
